

























































import { Component, Emit, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { DeviceTemplate } from "@/data/models/DeviceTemplate";
import { 
  DeviceAttributeType, 
  DevicePowerUsageType, 
  getDeviceAttributeTypeFromDeviceTemplate, 
  getDevicePowerUsageNameByType 
} from "@/data/models/DeviceAttribute";
import { addEditTemplate } from "@/data/models/DeviceSettingsManager";
import { DeviceCategoryTemplate } from "@/data/models/DeviceCategoryTemplate";
import { parsePowerConsumption } from "@/data/models/BaseDeviceInfo";
import PickerDialogComponent from "../common/PickerDialog.vue";
import ConfirmationDialogComponent from "../common/ConfirmationDialog.vue";
import { defineCategoriesAndTemplatesClientAccess } from "@/permissions/ClientPermissions";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";

@Component({
  methods: {},
  components: { 
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    picker: PickerDialogComponent,
  },
})
export default class DeviceTemplateDialog extends Vue {
  @Ref("baseDialog")
  baseDialog: BaseDialogComponent;

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  deviceTemplate: DeviceTemplate | null = null;
  deviceName = "";
  powerConsumptionPerHour: number | null = null;
  isEditable = false;
  isEditing = false;
  isNew = false;
  isLoading = false;
  categoryId = "";
  categoryTemplate: DeviceCategoryTemplate | null = null;
  selectedPowerUsageType: DevicePowerUsageType = DevicePowerUsageType.NONE;

  errorPowerConsumptionPerHourError: string[] = [];
  errorDeviceNameEmpty: string[] = [];

  hideSaveButton = false;
  restrictEditing = false;

  initialDeviceName = "";
  initialPowerUsageType = DevicePowerUsageType.NONE;
  initialPowerConsumptionPerHour: number | null = null;
  preventClosingDialog = this.isEditing;

  @Watch("hasDetailsEdited")
  onDetailsChanged() {
    if (this.isEditing) {
      this.baseDialog.negativeButtonText = this.hasDetailsEdited
        ? this.$t("common.abort").toString()
        : this.$t("common.close").toString();
    }
  }

  @Emit("onSuccess")
  onSuccess(message: string) {
    this.$emit("onSuccess", message);
    this.stopLoading();
    this.baseDialog.isShowing = false;
  }

  @Emit("onError")
  onError(message: string) {
    this.$emit("onError", message);
    this.stopLoading();
  }

  get powerConsumptionFormatter() {
    return (devicePowerUsageType: DevicePowerUsageType) => {
      return getDevicePowerUsageNameByType(this, devicePowerUsageType);
    };
  }

  get powerConsumptionOptions() {
    return [
      DevicePowerUsageType.NONE,
      DevicePowerUsageType.OPERATING_HOURS,
      DevicePowerUsageType.ELECTRICITY_METER,
      DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES
    ];
  }

  get hasDetailsEdited() {
    return this.initialDeviceName !== this.deviceName ||
      this.initialPowerUsageType !== this.selectedPowerUsageType ||
      this.initialPowerConsumptionPerHour !== this.powerConsumptionPerHour;
  }

  get isAllowedToDefineOwnDeviceCategoriesAndTemplates() {
    return defineCategoriesAndTemplatesClientAccess();
  }

  get sharedDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.sharedDeviceCategoriesAndTemplates;
  }

  get localDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.localDeviceCategoriesAndTemplates;
  }

  get deviceSettingsJson() {
    return this.isAllowedToDefineOwnDeviceCategoriesAndTemplates
      ? this.localDeviceSettingsJson
      : this.sharedDeviceSettingsJson;
  }

  async show(options: DeviceTemplateDialogOptions) {
    this.isNew = options.isNew ?? false;
    this.hideSaveButton = options.hideSaveButton ?? false;
    
    this.deviceTemplate = options.template ?? this.deviceTemplate;
    this.deviceName = this.deviceTemplate?.deviceName ?? "";
    this.categoryTemplate = options.categoryTemplate ?? null;
    const powerConsumptionPerHour = this.deviceTemplate?.deviceAttributes?.find(
        (attr) => attr.attributeType == DeviceAttributeType.POWER_CONSUMPTION_PER_HOUR)?.defaultValue ?? null;
    this.powerConsumptionPerHour = parsePowerConsumption(powerConsumptionPerHour ?? "");

    this.isEditable = options.isEditable ?? false;
    this.restrictEditing = options.restrictEditing ?? false;
    this.selectedPowerUsageType = this.deviceTemplate 
      ? getDeviceAttributeTypeFromDeviceTemplate(this.deviceTemplate)
      : DevicePowerUsageType.NONE;

    this.initialDeviceName = this.deviceName;
    this.initialPowerUsageType = this.selectedPowerUsageType;
    this.initialPowerConsumptionPerHour = this.powerConsumptionPerHour;

    const positiveButtonText = this.isNew
      ? this.$t("common.save").toString()
      : this.isEditable 
        ? this.$t("common.edit").toString() 
        : null;
    
    const negativeButtonText = this.$t("common.close").toString();
    
    this.baseDialog.show({
      title: this.$t("allDevices.editDeviceType").toString(),
      positiveButtonText: positiveButtonText,
      negativeButtonText: negativeButtonText,
      closeOnSave: false,
      hideSaveButton: this.hideSaveButton
    });
    
    if (this.isNew) {
      this.isEditable = true;
      this.onStartEdit();
    }
    
    this.resetErrors();
  }

  onDialogClosed() {
    this.deviceTemplate = null;
    this.deviceName = "";
    this.selectedPowerUsageType = DevicePowerUsageType.NONE;
    this.isEditable = false;
    this.isEditing = false;
    this.powerConsumptionPerHour = null;
    this.isNew = false;
    this.preventClosingDialog = false;
  }

  onPositiveButtonClick() {
    if (!this.isEditing && this.isEditable) {
      this.onStartEdit();
    } else if (this.isEditing) {
      this.onSubmit();
    }
  }

  onStartEdit() {
    this.resetErrors();
    this.isEditing = true;
    this.preventClosingDialog = true;

    this.baseDialog.positiveButtonText = this.$t("common.save").toString();
    this.baseDialog.negativeButtonText = this.$t("common.close").toString();
  }

  resetErrors() {
    this.errorDeviceNameEmpty = [];
    this.errorPowerConsumptionPerHourError = [];
  }

  async onSubmit() {
    this.startLoading();

    if (!this.deviceName || this.deviceName === "") {
      this.errorDeviceNameEmpty.push(this.$t("allDevices.errorDeviceNameEmpty").toString());
      this.onError("allDevices.deviceTemplateNotSaved");
      return;
    }

    if (!this.validateDeviceName()) {
      this.errorDeviceNameEmpty.push(this.$t("allDevices.errorExistingDeviceName").toString());
      this.onError("allDevices.deviceTemplateNotSaved");
      return;
    }

    if (!this.categoryTemplate) {
      this.onError("allDevices.deviceTemplateNotSaved");
      return;
    }

    const deviceSettings = this.deviceSettingsJson;

    const updatedSettings = await addEditTemplate(
      true, 
      this.categoryTemplate.id, 
      this.deviceName, 
      this.selectedPowerUsageType, 
      deviceSettings,
      this.deviceTemplate, 
      this.powerConsumptionPerHour
    );

    if (!updatedSettings) {
      this.onError("allDevices.errorCategoryNotFound")
    }

    this.onSuccess("allDevices.deviceTemplateSaved");
  }

  async onAbortEditDeviceDetails() {
    if (this.isEditing) {
      if (this.hasDetailsEdited) {
        const result = await this.confirmationDialogComponent.show({
          title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
          message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
          rejectMessage: this.$t("common.back").toString(),
          acceptMessage: this.$t("common.abort").toString(),
        });

        if (result) {
          this.baseDialog.isShowing = false;
          this.preventClosingDialog = false;
        }
      }
      else {
        this.baseDialog.isShowing = false;
        this.preventClosingDialog = false;
      }
    }
  }

  validateDeviceName(): boolean {
    return !this.categoryTemplate?.deviceTemplates?.some((temp) => {
      if (temp.id !== this.deviceTemplate?.id) {
        return temp.deviceName.toLowerCase() == this.deviceName.toLowerCase();
      }
    }) ?? true;
  }

  startLoading() {
    this.isLoading = true;
  }

  stopLoading() {
    this.isLoading = false;
  }
}

interface DeviceTemplateDialogOptions {
  template?: DeviceTemplate | null;
  isEditable?: boolean | null;
  isNew?: boolean | null;
  categoryTemplate?: DeviceCategoryTemplate | null;
  hideSaveButton?: boolean;
  restrictEditing?: boolean;
}
